import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { setProgressData } from "../store/progressData/progressDataSlice";
import { useProgress } from "./useProgress";

/**
 * usePageLoader
 * - While `isLoading` is true, increments progress up to `maxWhileLoading`.
 * - When `isLoading` flips to false, clears the interval and sets progress to 100.
 *
 * @param {boolean} isLoading
 * @param {{ step?: number, tick?: number, maxWhileLoading?: number }} options
 */
export const usePageLoader = (
  isLoading,
  { step = 10, tick = 150, maxWhileLoading = 90 } = {}
) => {
  const { progressFunction } = useProgress();
  const dispatch = useDispatch();

  const intervalRef = useRef(null);
  const countRef = useRef(0);

  useEffect(() => {
    const clear = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    if (isLoading) {
      countRef.current = 0;
      dispatch(setProgressData(0));

      clear();

      intervalRef.current = setInterval(() => {
        const next = countRef.current + step;
        if (next < maxWhileLoading) {
          countRef.current = next;
          dispatch(setProgressData(next));
        } else {
          countRef.current = maxWhileLoading;
          dispatch(setProgressData(maxWhileLoading));
          clear();
        }
      }, tick);

      if (typeof progressFunction === "function") {
        progressFunction(intervalRef.current);
      }
    } else {
      clear();
      countRef.current = 100;
      dispatch(setProgressData(100));
    }

    return () => clear();
  }, [isLoading, step, tick, maxWhileLoading]);
};
